import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { db } from '../firebase';
import { doc, setDoc } from 'firebase/firestore';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Grid
} from '@mui/material';
import logo from '../assets/gemistruct-logo.png';
import wordmark from '../assets/gemistruct-wordmark.png';
import sydneyBg from '../assets/sydney-background.jpg';

export default function SignUp() {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  async function handleSubmit(e) {
    e.preventDefault();
    if (password !== confirmPassword) {
      return setError('Passwords do not match');
    }
    if (!fullName || !company) {
      return setError('Please fill in all fields');
    }
    try {
      setError('');
      setLoading(true);
      const userCredential = await signup(email, password);
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        fullName,
        company,
        email
      });
      navigate('/dashboard');
    } catch (err) {
      setError('Failed to create an account: ' + err.message);
    }
    setLoading(false);
  }

  return (
    <Container maxWidth="xl" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
      <Grid container spacing={4}>
        {/* Left Side - Sign Up Form */}
        <Grid item xs={12} md={6}>
          <Box sx={{ p: 4, maxWidth: 500, margin: 'auto' }}>
            <Box 
              component={Link}
              to="/"
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 6,
                textDecoration: 'none',
                '&:hover': { opacity: 0.9 }
              }}
            >
              <img src={logo} alt="Gemistruct Logo" style={{ height: 48, marginRight: 16 }} />
              <img src={wordmark} alt="Gemistruct" style={{ height: 36 }} />
            </Box>

            <Typography variant="h4" component="h1" gutterBottom fontWeight={700}>
              Sign Up
            </Typography>
            <Typography color="text.secondary" gutterBottom>
              Already have an account? <Link to="/signin" style={{ color: '#1976d2', textDecoration: 'none' }}>Sign In</Link>
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <form onSubmit={handleSubmit}>
              <TextField
                margin="normal"
                required
                fullWidth
                label="Full Name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="Company, University or School"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{ mt: 3, mb: 2, py: 1.5, fontSize: '1.1rem' }}
                disabled={loading}
              >
                Sign Up
              </Button>

              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 3 }}>
                By signing up, I agree to the{' '}
                <Link to="/terms" style={{ color: '#1976d2', textDecoration: 'none' }}>
                  Terms of Use
                </Link>
              </Typography>
            </form>
          </Box>
        </Grid>

        {/* Right Side - Marketing Content */}
        <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
          <Box
            sx={{
              height: '100%',
              bgcolor: 'transparent',
              backgroundImage: `linear-gradient(rgba(0, 47, 108, 0.4), rgba(0, 47, 108, 0.7)), url(${sydneyBg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderRadius: 2,
              p: 6,
              color: 'white',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Typography variant="h2" gutterBottom fontWeight={700} sx={{ fontSize: { md: '3rem', lg: '3.75rem' } }}>
              Next-Gen Structural Engineering Software
            </Typography>
            <Typography variant="h5" sx={{ mb: 4, lineHeight: 1.5 }}>
              Lightning-fast, cloud-based design tools delivering instant results for beams, columns, footings, and connections—all compliant with Australian Standards.
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
} 